import React, { useEffect, useMemo, useState } from 'react';
import { listSnapshotDays, fetchSnapshotRows, SnapshotDay, ProductSnapRow } from '@/services/productSnapshots';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

// Check if we're actually on a native platform (iOS/Android), not in a web browser
function isNativePlatform() {
  try {
    // First check: if we're clearly in a web browser, return false
    const userAgent = navigator.userAgent.toLowerCase();
    const isWebBrowser = userAgent.includes('chrome') || 
                         userAgent.includes('firefox') || 
                         userAgent.includes('safari') || 
                         userAgent.includes('edge') ||
                         userAgent.includes('opera');
    
    // If we're in a web browser on Windows/Mac/Linux, definitely not native
    const platform = navigator.platform.toLowerCase();
    const isDesktopOS = platform.includes('win') || 
                        platform.includes('mac') || 
                        platform.includes('linux');
    
    if (isWebBrowser && isDesktopOS) {
      return false;
    }
    
    // Second check: Capacitor native platform check
    const Capacitor = (window as any).Capacitor;
    if (Capacitor && typeof Capacitor.isNativePlatform === 'function') {
      return Capacitor.isNativePlatform();
    }
    return false;
  } catch {
    return false;
  }
}

async function saveXlsxFile(wb: XLSX.WorkBook, filename: string) {
  // Check if we're on a native platform (iOS/Android)
  // Only use Capacitor Share API on actual mobile devices, never in web browsers
  const isNative = isNativePlatform();
  
  if (isNative) {
    try {
      const [{ Filesystem, Directory, Encoding }, { Share }] = await Promise.all([
        import('@capacitor/filesystem'),
        import('@capacitor/share')
      ]);
      
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      
      // Convert to base64
      let base64 = '';
      const bytes = new Uint8Array(wbout);
      for (let i = 0; i < bytes.byteLength; i++) {
        base64 += String.fromCharCode(bytes[i]);
      }
      base64 = btoa(base64);
      
      const dir = Directory.Documents;
      
      await Filesystem.mkdir({ 
        path: 'exports', 
        directory: dir, 
        recursive: true 
      }).catch(() => {});
      
      const path = `exports/${filename}`;
      await Filesystem.writeFile({ 
        path, 
        data: base64, 
        directory: dir, 
        encoding: Encoding.BASE64 
      });
      
      const { uri } = await Filesystem.getUri({ path, directory: dir });
      await Share.share({ 
        title: filename, 
        text: 'Ürünler snapshot', 
        url: uri, 
        dialogTitle: 'Paylaş' 
      });
      return;
    } catch (error) {
      console.warn('Capacitor file save failed, falling back to web download:', error);
      // Fall through to web download
    }
  }
  
  // Web browsers - always use XLSX.writeFile for direct download
  // This will trigger the browser's download functionality
  XLSX.writeFile(wb, filename);
}

interface ProductsSnapshotExportProps {
  companyId: string;
}

export default function ProductsSnapshotExport({ companyId }: ProductsSnapshotExportProps) {
  const [days, setDays] = useState<SnapshotDay[]>([]);
  const [loadingDays, setLoadingDays] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        setLoadingDays(true);
        const d = await listSnapshotDays(companyId);
        setDays(d);
        setSelected(d[0]?.snapshot_date ?? null);
      } catch (e: any) {
        toast({
          title: "Hata",
          description: e.message ?? 'Gün listesi alınamadı',
          variant: "destructive",
        });
      } finally {
        setLoadingDays(false);
      }
    })();
  }, [companyId, toast]);

  const selectedLabel = useMemo(() => {
    const f = days.find(x => x.snapshot_date === selected);
    if (!f) return '';
    return new Date(f.snapshot_date).toLocaleDateString('tr-TR', { 
      dateStyle: 'long',
      timeZone: 'Europe/Istanbul' 
    });
  }, [days, selected]);

  async function onExport() {
    if (!selected) return;
    
    setExporting(true);
    try {
      const rows = await fetchSnapshotRows(companyId, selected);
      
      // Map to Excel columns matching the SQL query structure
      // Include all rows, including those with on_hold_quantity > 0
      const table = rows.map(r => ({
        'Snapshot Tarihi': r.snapshot_date,
        'Ürün Kodu': r.item_code ?? '',
        'Kategori': r.category ?? '',
        'İsim': r.name,
        'Elde Miktar': r.quantity_on_hand ?? 0,
        'Beklemedeki Miktar': r.on_hold_quantity ?? 0,
        'Fiyat': r.price ?? null,
        'Başlangıç Maliyeti': r.initial_cost ?? null,
        'Güncel Maliyet': r.current_cost ?? null,
        'Lokasyon ID': r.location_id ?? null,
      }));
      
      const ws = XLSX.utils.json_to_sheet(table);
      const wb = XLSX.utils.book_new(); 
      XLSX.utils.book_append_sheet(wb, ws, 'Stok Snapshot');
      
      const dateStr = selected.replace(/-/g, '');
      await saveXlsxFile(wb, `inventory_snapshot_${dateStr}.xlsx`);
      
      toast({
        title: "Başarılı",
        description: `${rows.length} ürün dışa aktarıldı`,
      });
    } catch (e: any) {
      toast({
        title: "Hata",
        description: e.message ?? 'Dışa aktarım hatası',
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Ürünler Snapshot Export</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Günlük ürün snapshot'larını Excel formatında dışa aktarın (02:00 TR)
        </p>
      </div>

      {loadingDays ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Snapshot tarihleri yükleniyor...
        </div>
      ) : days.length === 0 ? (
        <div className="text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg">
          Henüz ürün snapshot'ı bulunmuyor.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="snapshot-select">Snapshot Tarihi Seç</Label>
            <Select value={selected ?? ''} onValueChange={(value) => setSelected(value || null)}>
              <SelectTrigger id="snapshot-select">
                <SelectValue placeholder="Bir tarih seçin..." />
              </SelectTrigger>
              <SelectContent>
                {days.map(d => (
                  <SelectItem key={d.snapshot_date} value={d.snapshot_date}>
                    {new Date(d.snapshot_date).toLocaleDateString('tr-TR', { 
                      dateStyle: 'long',
                      timeZone: 'Europe/Istanbul' 
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedLabel && (
            <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded">
              <strong>Seçilen:</strong> {selectedLabel}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button 
              onClick={onExport}
              disabled={!selected || exporting}
              className="flex items-center gap-2"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Hazırlanıyor...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Excel'e Aktar
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
